import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/api';
import { useAuthContext } from '../context/AuthContext';

export function PaymentReturn() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const authContext = useAuthContext();
  const token = authContext?.token;
  const headers = authContext?.headers;
  const [status, setStatus] = useState('processing');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  
  // Get params from URL
  const urlPaymentId = searchParams.get('payment_id');
  const result = searchParams.get('result'); // Swish callback result (JSON encoded)
  const legacyStatus = searchParams.get('status'); // Legacy param
  const requestId = searchParams.get('request_id'); // For P2P requests
  
  // Also check sessionStorage for pending payment
  const storedPaymentId = typeof window !== 'undefined' ? sessionStorage.getItem('pending_payment_id') : null;
  const paymentId = urlPaymentId || storedPaymentId;

  // Guard against React StrictMode double-mount firing the callback POST twice
  // (root cause of duplicate transactions). Ref persists across StrictMode's
  // simulated remount, so processReturn() runs exactly once.
  const didProcess = useRef(false);

  useEffect(() => {
    if (didProcess.current) return;
    didProcess.current = true;
    const processReturn = async () => {
      // Get token from context or localStorage
      const authToken = token || localStorage.getItem('token');
      
      // Build headers
      const authHeaders = authToken 
        ? { Authorization: `Bearer ${authToken}` }
        : null;
      
      console.log('PaymentReturn processing:', { paymentId, result, urlPaymentId, storedPaymentId, hasAuth: !!authToken });
      
      // Clear stored payment ID
      if (storedPaymentId) {
        sessionStorage.removeItem('pending_payment_id');
      }
      
      // If no payment ID and no auth, show login prompt
      if (!paymentId && !authToken) {
        setStatus('unknown');
        setNeedsLogin(true);
        return;
      }
      
      // If we have payment ID but no auth, we can still try to show info
      // and prompt for confirmation after login
      if (!authHeaders) {
        setStatus('unknown');
        setNeedsLogin(true);
        return;
      }
      
      // First, try to get payment info
      if (paymentId && authHeaders) {
        try {
          const paymentRes = await axios.get(`${API}/payment/${paymentId}`, { headers: authHeaders });
          setPaymentInfo(paymentRes.data);
          
          // If payment is already completed, show success
          if (paymentRes.data.status === 'COMPLETED') {
            setStatus('success');
            toast.success('Payment already completed!');
            return;
          }
        } catch (e) {
          console.log('Could not fetch payment info:', e.response?.status);
          if (e.response?.status === 401) {
            setNeedsLogin(true);
            setStatus('unknown');
            return;
          }
        }
      }
      
      // Check if we have a Swish callback result
      if (result && paymentId && authHeaders) {
        try {
          console.log('Processing Swish callback result...');
          // Decode result if needed (it might be URL encoded)
          let decodedResult = result;
          try {
            if (result.includes('%')) {
              decodedResult = decodeURIComponent(result);
            }
          } catch (e) {}
          
          // Send callback result to backend
          const res = await axios.post(`${API}/payment/${paymentId}/callback`, {
            result: decodedResult
          }, { headers: authHeaders });
          
          console.log('Callback response:', res.data);
          
          if (res.data.payment_status === 'COMPLETED') {
            setStatus('success');
            setPaymentDetails(res.data);
            toast.success('Payment completed successfully!');
          } else if (res.data.payment_status === 'CANCELLED') {
            setStatus('failed');
            toast.error('Payment was cancelled');
          } else {
            setStatus('error');
            toast.error('Payment could not be processed');
          }
        } catch (e) {
          console.error('Callback error:', e);
          setStatus('unknown');
        }
      } else if (paymentId) {
        // No result param - user returned manually from Swish
        setStatus('unknown');
        toast.info('Please confirm if you completed the payment');
      } else if (legacyStatus) {
        // Legacy status handling
        const normalizedStatus = legacyStatus.toLowerCase();
        if (normalizedStatus === 'paid' || normalizedStatus === 'paid_manual') {
          setStatus('success');
          toast.success('Payment completed!');
        } else if (normalizedStatus === 'declined' || normalizedStatus === 'cancelled' || normalizedStatus === 'notpaid') {
          setStatus('failed');
          toast.error('Payment was not completed');
        }
        setTimeout(() => nav('/'), 3000);
      } else {
        // No params
        toast.info('Returned from Swish');
        setTimeout(() => nav('/'), 2000);
      }
    };
    
    processReturn();
  }, []);

  const handleConfirmPayment = async () => {
    const authToken = token || localStorage.getItem('token');
    
    if (!authToken) {
      toast.error('Please log in to confirm the payment');
      nav('/?redirect=/payment-return?payment_id=' + paymentId);
      return;
    }
    
    if (!paymentId) {
      toast.error('No payment to confirm');
      return;
    }
    
    setStatus('processing');
    
    const authHeaders = { Authorization: `Bearer ${authToken}` };
    
    try {
      const res = await axios.post(`${API}/payment/${paymentId}/mark-completed`, {}, { headers: authHeaders });
      setStatus('success');
      setPaymentDetails(res.data);
      toast.success('Payment marked as completed!');
      
      setTimeout(() => nav('/'), 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm payment');
      setStatus('error');
    }
  };

  const handleCancelPayment = async () => {
    const authToken = token || localStorage.getItem('token');
    
    if (!paymentId) {
      nav('/');
      return;
    }
    
    if (authToken) {
      const authHeaders = { Authorization: `Bearer ${authToken}` };
      try {
        await axios.delete(`${API}/payment/${paymentId}`, { headers: authHeaders });
        toast.info('Payment cancelled');
      } catch (e) {}
    }
    
    nav('/');
  };
  
  const handleLogin = () => {
    // Store current URL for redirect after login
    sessionStorage.setItem('post_login_redirect', window.location.href);
    nav('/');
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'failed':
        return <XCircle className="h-16 w-16 text-red-500" />;
      case 'error':
        return <AlertCircle className="h-16 w-16 text-orange-500" />;
      case 'unknown':
        return <AlertCircle className="h-16 w-16 text-blue-500" />;
      default:
        return <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'success':
        return 'Payment Successful!';
      case 'failed':
        return 'Payment Not Completed';
      case 'error':
        return 'Payment Error';
      case 'unknown':
        return 'Did you complete the payment?';
      default:
        return 'Processing Payment...';
    }
  };

  const getStatusDescription = () => {
    switch (status) {
      case 'success':
        return 'Your payment has been recorded. The recipient will be notified if they use PayMig.';
      case 'failed':
        return 'The payment was cancelled or declined.';
      case 'error':
        return 'There was an error processing your payment. Please try again.';
      case 'unknown':
        return 'We could not automatically detect if you completed the payment in Swish. Please confirm below.';
      default:
        return 'Please wait while we confirm your payment status...';
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'success':
        return 'bg-green-50';
      case 'failed':
        return 'bg-red-50';
      case 'error':
        return 'bg-orange-50';
      case 'unknown':
        return 'bg-blue-50';
      default:
        return 'bg-gray-50';
    }
  };

  return (
    <div className={`min-h-screen ${getBgColor()} flex items-center justify-center p-4`}>
      <Card className="w-full max-w-md" data-testid="payment-return-page">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className="text-xl">{getStatusText()}</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            {getStatusDescription()}
          </p>
          
          {/* Show payment info if available */}
          {paymentInfo && (
            <div className="mt-2 p-3 bg-white rounded-lg border text-left text-sm">
              <p><strong>Amount:</strong> {paymentInfo.amount} {paymentInfo.currency || 'SEK'}</p>
              <p><strong>To:</strong> {paymentInfo.recipient?.name}</p>
              {paymentInfo.description && (
                <p><strong>Description:</strong> {paymentInfo.description}</p>
              )}
            </div>
          )}
          
          {paymentId && (
            <p className="text-xs text-muted-foreground">
              Payment ID: {paymentId.substring(0, 8)}...
            </p>
          )}
          
          {/* Show confirmation buttons for unknown status */}
          {status === 'unknown' && !needsLogin && (
            <div className="flex flex-col gap-3 mt-6">
              <Button onClick={handleConfirmPayment} className="w-full" data-testid="confirm-payment-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Yes, I completed the payment
              </Button>
              <Button onClick={handleCancelPayment} variant="outline" className="w-full" data-testid="cancel-payment-btn">
                <XCircle className="h-4 w-4 mr-2" />
                No, cancel this payment
              </Button>
            </div>
          )}
          
          {/* Show login prompt */}
          {needsLogin && (
            <div className="flex flex-col gap-3 mt-6">
              <p className="text-sm text-muted-foreground">
                Please log in to confirm your payment.
              </p>
              <Button onClick={handleLogin} className="w-full">
                Log in to PayMig
              </Button>
              <Button onClick={() => nav('/')} variant="outline" className="w-full">
                Return Home
              </Button>
            </div>
          )}
          
          {/* Return button for other statuses */}
          {status !== 'unknown' && status !== 'processing' && (
            <Button 
              onClick={() => nav('/')} 
              variant="outline"
              className="mt-4"
              data-testid="return-dashboard-btn"
            >
              Return to Dashboard
            </Button>
          )}
          
          {/* Show payment details if available */}
          {paymentDetails && status === 'success' && (
            <div className="mt-4 p-3 bg-green-100 rounded-lg text-sm">
              <p className="font-medium">Transaction recorded!</p>
              <p className="text-muted-foreground">
                View it in your transaction history.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
